import React, { useEffect, useState } from 'react'
import { Modal, Frame, Button } from '@react95/core'
import { Heading, Text } from '@pancakeswap/uikit'
import { Progman39 } from '@react95/icons'
import { useTranslation } from 'contexts/Localization'
import { useTokenBalances } from 'state/wallet/hooks'
import { TokenAmount } from '@pancakeswap/sdk'
import tokens from 'config/constants/tokens'
import { useWeb3React } from '@web3-react/core'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { Contract } from 'ethers'
import { getWindogeMigrationContract } from 'utils/contractHelpers'
import truncateHash from 'utils/truncateHash'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { AutoRow } from 'components/Layout/Row'
import CircleLoader from 'components/Loader/CircleLoader'
import { tryParseAmount } from 'state/swap/hooks'
import { useCurrency } from 'hooks/Tokens'
import Column from 'components/Layout/Column'
import useMigrationAccount from 'hooks/useMigrationAccount'
import { FetchStatus } from 'hooks/useTokenBalance'

const MigrationModal = ({ close, modalStyle, frameStyle }) => {
  const { t } = useTranslation()
  const { account, library } = useWeb3React()
  const truncatedAddress = truncateHash(account ?? '')
  const windoge95 = useCurrency(tokens.windoge95.address)
  const balances = useTokenBalances(account, [tokens.bnb, tokens.busd, tokens.windoge95])
  const migrationAccount = useMigrationAccount()

  const windogeBalance: undefined | TokenAmount = balances?.[tokens.windoge95.address]
  const buttonStyle: any = { marginBottom: '24px', fontSize: '24px' }
  const approvalAmount = tryParseAmount('1000000000', windoge95)
  const [approval, approveCallback] = useApproveCallback(approvalAmount, '0x2ac04ae31ab9831960eefcb4ee99a2cb5d46052c')
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  const depositedTokens =
    migrationAccount?.fetchStatus === FetchStatus.NOT_FETCHED
      ? 'Loading...'
      : migrationAccount?.fetchStatus === FetchStatus.FAILED
      ? 'Error fetching balance'
      : `${(Number.parseInt((migrationAccount?.migrationAccount?.depositedV1 || 0).toString()) / 1000000000)
          .toFixed(0)
          .toString()}`

  const claimableTokens =
    migrationAccount?.fetchStatus === FetchStatus.NOT_FETCHED
      ? 'Loading...'
      : migrationAccount?.fetchStatus === FetchStatus.FAILED
      ? 'Error fetching balance'
      : `${(Number.parseInt((migrationAccount?.migrationAccount?.claimableV2 || 0).toString()) / 1000000000)
          .toFixed(2)
          .toString()}`

  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])

  const withdrawV2Tokens = async () => {
    try {
      const contract: Contract = getWindogeMigrationContract()
      const signer = contract.connect(library.getSigner())
      await signer.claimV2Tokens()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Modal
      defaultPosition={{ x: 12, y: 12 }}
      icon={<Progman39 />}
      title="V2 Migration"
      closeModal={close}
      width="90vw"
      height="90vh"
      style={modalStyle}
      buttons={[{ value: 'Ok', onClick: close }]}
    >
      <Frame style={frameStyle}>
        <Heading scale="xl" mb="16px" mt="16px">
          {t(`Migrate your Windoge95 tokens to V2 Tokens`)}
        </Heading>
        <Text fontSize="18px" mb="24px">
          {t(`Use this simple interface to migrate your Windoge95 tokens to V2 Tokens.`)}
        </Text>
        <Text fontSize="18px">{t(`Step 1: Connect your wallet`)}</Text>
        <Text fontWeight="bold" fontSize="24px" mb="24px">
          {account && t('Connected with %address%', { address: truncatedAddress })}
        </Text>
        {account && (
          <>
            <Text fontSize="24px" fontWeight="bold">
              {t(`Your Windoge95 V1 Balance:`)}
            </Text>
            <Text fontSize="24px" fontWeight="bold">
              {windogeBalance?.toFixed(2)} WNDG
            </Text>
          </>
        )}
        {!account && <ConnectWalletButton />}
        <Text mt="24px" fontSize="18px">
          {t(`Step 2: Enable Windoge95 V1 to use the Migration Contract`)}
        </Text>
        <Button
          style={buttonStyle}
          onClick={approveCallback}
          disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted}
        >
          {approval === ApprovalState.PENDING ? (
            <AutoRow gap="6px" justify="center">
              {t('Enabling')} <CircleLoader stroke="white" />
            </AutoRow>
          ) : approval === ApprovalState.APPROVED ? (
            t('Enabled')
          ) : (
            t('Enable %asset%', { asset: tokens.windoge95.symbol ?? '' })
          )}
        </Button>
        <AutoRow gap="6px" justify="space-between">
          <Column>
            <Text fontSize="18px">{t(`Step 3: Deposit your Windoge95 V1 tokens`)}</Text>
            <Button
              disabled={
                !account ||
                Number.parseInt((migrationAccount?.migrationAccount?.claimableV2 || 0).toString()) / 1000000000 > 0 ||
                windogeBalance?.equalTo('0')
              }
              onClick={async () => {
                try {
                  const contract: Contract = getWindogeMigrationContract(library.getSigner())
                  await contract.depositTokensV1({ from: account })
                } catch (e) {
                  console.error(e)
                }
              }}
              style={buttonStyle}
            >
              Deposit V1 Tokens
            </Button>
          </Column>
          <Column>
            <Text fontSize="18px">{t(`Total Deposited V1 Tokens (by all users):`)}</Text>
            <Text fontSize="24px" fontWeight="bold">
              {depositedTokens} WNDG
            </Text>
          </Column>
        </AutoRow>
        <AutoRow gap="6px" justify="space-between">
          <Column>
            <Text fontSize="18px">{t(`Step 4: Claim your Windoge95 V2 tokens`)}</Text>
            <Button disabled onClick={withdrawV2Tokens} style={buttonStyle}>
              Withdraw V2 Tokens (Coming Soon)
            </Button>
          </Column>
          <Column>
            <Text fontSize="18px">{t(`Claimable V2 Tokens`)}</Text>
            <Text fontSize="24px" fontWeight="bold">
              {claimableTokens} WNDG V2
            </Text>
          </Column>
        </AutoRow>
      </Frame>
    </Modal>
  )
}

export default MigrationModal
